import {
  AppBar, Avatar, Badge, Box, Chip, Divider, Drawer, IconButton, List, ListItem,
  ListItemButton, ListItemIcon, ListItemText, Menu, MenuItem, Stack, Toolbar,
  Tooltip, Typography, useMediaQuery, useTheme,
} from '@mui/material';
import { Logout, Notifications as NotifIcon, Menu as MenuIcon } from '@mui/icons-material';
import { useEffect, useState } from 'react';
import { Outlet, useNavigate, NavLink, useLocation } from 'react-router-dom';
import { useAuthStore } from '../features/auth/store';
import { useLogout } from '../features/auth/queries';
import { navItemsForRole } from './sidebarConfig';
import Logo from '../shared/Logo';
import { ROLE_LABELS } from '../shared/constants';
import { initials, roleColor } from '../shared/utils';
import { useQuery } from '@tanstack/react-query';
import { api } from '../shared/api';
import type { Notification } from '../shared/types';

const DRAWER_WIDTH = 260;

export default function PortalLayout() {
  const user = useAuthStore((s) => s.user);
  const logoutMutation = useLogout();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [menuEl, setMenuEl] = useState<null | HTMLElement>(null);
  const location = useLocation();

  const items = user ? navItemsForRole(user.role) : [];

  const notifQuery = useQuery({
    queryKey: ['notifications'],
    queryFn: async () => (await api.get<Notification[]>('/notifications')).data,
    refetchInterval: 30_000,
  });
  const unread = (notifQuery.data ?? []).filter((n) => !n.readAt).length;

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  if (!user) return null;

  const drawer = (
    <Box sx={{ width: DRAWER_WIDTH, height: '100%', bgcolor: 'primary.dark', color: '#fff', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center' }}>
        <Logo variant="inverse" />
      </Box>
      <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />
      <Box sx={{ p: 2 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Avatar sx={{ bgcolor: roleColor(user.role) }}>{initials(user.fullName)}</Avatar>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>{user.fullName}</Typography>
            <Chip size="small" label={ROLE_LABELS[user.role]} sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: '#fff', height: 20, fontSize: 11 }} />
          </Box>
        </Stack>
      </Box>
      <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />
      <List sx={{ flexGrow: 1, py: 1 }}>
        {items.map((it) => (
          <ListItem key={`${it.to}-${it.label}`} disablePadding sx={{ px: 1, mb: 0.25 }}>
            <ListItemButton
              component={NavLink} to={it.to} end={it.to === '/portal'}
              sx={{
                borderRadius: 2, color: '#fff',
                '&.active': { bgcolor: 'rgba(255,255,255,0.15)' },
                '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' },
              }}
            >
              <ListItemIcon sx={{ color: '#fff', minWidth: 40 }}>{it.icon}</ListItemIcon>
              <ListItemText primary={it.label} primaryTypographyProps={{ fontSize: 14, fontWeight: 600 }} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />
      <Box sx={{ p: 1.5 }}>
        <ListItemButton
          onClick={() => logoutMutation.mutate(undefined, { onSettled: () => navigate('/login') })}
          sx={{ borderRadius: 2, color: '#fff', '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' } }}
        >
          <ListItemIcon sx={{ color: '#fff', minWidth: 40 }}><Logout /></ListItemIcon>
          <ListItemText primary="Sign out" primaryTypographyProps={{ fontSize: 14, fontWeight: 600 }} />
        </ListItemButton>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      {!isMobile && (
        <Drawer
          variant="permanent"
          PaperProps={{ sx: { border: 0, width: DRAWER_WIDTH } }}
          sx={{ width: DRAWER_WIDTH, flexShrink: 0 }}
        >
          {drawer}
        </Drawer>
      )}
      {isMobile && (
        <Drawer
          variant="temporary" open={mobileOpen} onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }} PaperProps={{ sx: { width: DRAWER_WIDTH, border: 0 } }}
        >
          {drawer}
        </Drawer>
      )}

      <Box sx={{ flexGrow: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <AppBar position="sticky" sx={{ bgcolor: '#fff', borderBottom: '1px solid #E2E8F0' }}>
          <Toolbar>
            {isMobile && (
              <IconButton onClick={() => setMobileOpen(true)} edge="start" sx={{ mr: 1 }}>
                <MenuIcon />
              </IconButton>
            )}
            <Typography variant="h6" sx={{ flexGrow: 1, color: 'text.primary', fontWeight: 700 }}>
              Baseras Portal
            </Typography>
            <Tooltip title="Notifications">
              <IconButton onClick={() => navigate('/portal/notifications')} sx={{ mr: 1 }}>
                <Badge badgeContent={unread} color="secondary"><NotifIcon /></Badge>
              </IconButton>
            </Tooltip>
            <IconButton onClick={(e) => setMenuEl(e.currentTarget)}>
              <Avatar sx={{ width: 32, height: 32, bgcolor: roleColor(user.role), fontSize: 14 }}>
                {initials(user.fullName)}
              </Avatar>
            </IconButton>
            <Menu open={!!menuEl} anchorEl={menuEl} onClose={() => setMenuEl(null)}>
              <MenuItem onClick={() => { setMenuEl(null); navigate('/portal/profile'); }}>Profile</MenuItem>
              <MenuItem onClick={() => { setMenuEl(null); logoutMutation.mutate(undefined, { onSettled: () => navigate('/login') }); }}>
                Sign out
              </MenuItem>
            </Menu>
          </Toolbar>
        </AppBar>
        <Box component="main" sx={{ p: { xs: 2, md: 3 }, flexGrow: 1 }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
}
